/*
   ****** The below React component is developed by AECS Private Limited. ******
   Please don't remove, replace, or edit this header information
   Component: <Alarm.js>
   Description: This React component handles the display of open alarms, including sorting and pagination. It also provides a switch to toggle between Valve Alarm 1 and Valve Alarm 2.
   Dependencies: This component uses Material-UI components, moment for date formatting, and custom components like Header, Footer, and Sidebar.
 */

   import React, { useState, useEffect } from 'react';
   // eslint-disable-next-line
   import { useNavigate } from 'react-router-dom';
   import { styled } from '@mui/material/styles';
   import '../css/Alarm.css';
   import Card from '@mui/material/Card';
   import CardContent from '@mui/material/CardContent';
   import { useLogoTitle } from '../classes/LogoTitleContext';
   import Button from '@mui/material/Button';
   import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
   import moment from 'moment';
   import Switch from '@mui/material/Switch';
   import Header from '../Parts/Header';
   import Footer from '../Parts/Footer';
   import Sidebar from '../Parts/Sidebar';
   import getAuthHeaders from '../Auth/Authentication';
   
   import {
     OnDeviceTraining as OnDeviceTrainingIcon,
     AlarmOn as AlarmOnIcon,
     Alarm as WarningIcon,
     Timer as TimerIcon,
   } from '@mui/icons-material';
   
   const StyledTableHeader = styled('th')(({ theme }) => ({
     padding: '15px',
     border: '1px solid #ddd',
     cursor: 'pointer',
     textAlign: 'center',
     verticalAlign: 'middle',
     backgroundColor: '#f2f2f2',
     borderBottom: '2px solid #ddd',
     height: '50px',
     fontSize: '16px',
     fontWeight: 'bold',
     color: '#333',
     transition: theme.transitions.create(['color', 'background-color'], {
       easing: theme.transitions.easing.sharp,
       duration: theme.transitions.duration.enteringScreen,
     }),
     '&:hover': {
       color: '#1976D2',
       backgroundColor: '#ddd',
     },
   }));
   
   
   const Alarm = ({ onLogout, loggedInUser }) => {
     // eslint-disable-next-line
     const navigate = useNavigate();
     const [switchState, setSwitchState] = useState(false); // Set an initial state based on your requirement
     //const [isSidebarOpen, setIsSidebarOpen] = useState(false);
     // eslint-disable-next-line
     const [isLoggedIn, setIsLoggedIn] = useState(/* initial login state */);
     const [data, setData] = useState([]);
     const [sortOrder, setSortOrder] = useState('asc');
     const [sortColumn, setSortColumn] = useState(null);
     const [currentPage, setCurrentPage] = useState(1);
     const itemsPerPage = 7;
     const totalPages = Math.ceil(data.length / itemsPerPage);
     const { logoUrl, projectTitle } = useLogoTitle();
     // eslint-disable-next-line
     const [anchorEl, setAnchorEl] = useState(null);
     const [username, setUsername] = useState(/* initial username */);
     const [userRole, setUserRole] = useState('');
     // eslint-disable-next-line
     const [dataSource, setDataSource] = useState('OpenAlarmData');
   
   
   
     useEffect(() => {
       if (loggedInUser) {
         const fetchData = async () => {
           try {
             // Update the endpoint based on the selected data source
             const endpoint = dataSource === 'OpenAlarmData' ? 'http://localhost:5002/OpenAlarmData' : 'http://localhost:5002/AllAlarms';
   
             const response = await fetch(endpoint, {
               method: 'GET',
               headers: getAuthHeaders()
             });
             if (response.ok) {
               const result = await response.json();
               setData(result);
             } else {
               // Handle error if needed
             }
           } catch (error) {
             // Handle error if needed
           }
         };
   
         fetchData();
   
         const storedUsername = sessionStorage.getItem('username');
         const storedUserRole = sessionStorage.getItem('userRole');
         if (storedUsername) {
           setUsername(storedUsername);
         }
         if (storedUserRole) {
           setUserRole(storedUserRole);
         }
   
         // Set up interval to refresh data every 5 seconds
         const refreshInterval = setInterval(fetchData, 5 * 1000); // 5 seconds in milliseconds
   
         // Clear the interval when the component is unmounted
         return () => clearInterval(refreshInterval);
       }
     }, [userRole, loggedInUser, dataSource]);
   
   
   
     const handlePageChange = (newPage) => {
       // Additional logic for handling page changes, if needed
       setCurrentPage(newPage);
     };
   
     const paginatedData = () => {
       let filteredAndSortedData = sortedData();
   
       if (switchState) {
         // If the switch is ON, show data for both Valve Alarm 1 and Valve Alarm 2
         filteredAndSortedData = filteredAndSortedData.filter(
           (row) => row['Valve Alarm'] === '1' || row['Valve Alarm'] === '2'
         );
       } else {
         // If the switch is OFF, show data only for Valve Alarm 2
         filteredAndSortedData = filteredAndSortedData.filter(
           (row) => row['Valve Alarm'] === '2'
         );
       }
   
       const startIndex = (currentPage - 1) * itemsPerPage;
       const endIndex = startIndex + itemsPerPage;
       return filteredAndSortedData.slice(startIndex, endIndex);
     };
   
     const renderPaginationNumbers = () => {
       const pageNumbers = [];
   
       for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
         pageNumbers.push(
           <Button
             key={i}
             variant={currentPage === i ? 'contained' : 'outlined'}
             onClick={() => handlePageChange(i)}
             style={{ margin: '0 3px', height: '25px', minWidth: '25px', fontSize: '12px' }}
           >
             {i}
           </Button>
         );
       }
   
       return pageNumbers;
     };
   
     const handleSwitchChange = () => {
       setSwitchState((prevSwitchState) => {
         // Toggle data source when the switch is changed
         const newDataSource = prevSwitchState ? 'OpenAlarmData' : 'AllAlarms';
         setDataSource(newDataSource);
   
         return !prevSwitchState;
       });
     };
   
     // eslint-disable-next-line
     const handleMenuClose = () => {
       setAnchorEl(null);
     };
   
     const handleSort = (columnName) => {
       setSortOrder((prevOrder) => (sortColumn === columnName ? (prevOrder === 'asc' ? 'desc' : 'asc') : 'asc'));
       setSortColumn(columnName);
     };
   
     const sortedData = () => {
       if (!sortColumn) {
         return data;
       }
   
       return data.slice().sort((a, b) => {
         const valueA = a[sortColumn];
         const valueB = b[sortColumn];
   
         if (valueA !== undefined && valueB !== undefined) {
           if (sortOrder === 'asc') {
             return valueA.localeCompare(valueB);
           } else {
             return valueB.localeCompare(valueA);
           }
         } else {
           return 0;
         }
       });
     };
     // eslint-disable-next-line
     const LogoutIcon = styled(PowerSettingsNewIcon)({
       marginRight: '8px',
     });
   
     return (
       <div style={{ backgroundColor: '#EBEFF3', minHeight: '100vh', }}>
         <Sidebar />
         <main>
           <Header
             logoUrl={logoUrl}
             loggedInUser={loggedInUser}
             onLogout={onLogout}
             username={username}
             userRole={userRole}
           />
           <div style={{ display: 'flex', justifyContent: 'center' }}>
             <form style={{ width: '80%' }}>
               <Card sx={{ width: '100%', maxWidth: 2000, padding: '30px', borderRight: '5px solid dodgerblue', borderBottom: '5px solid dodgerblue', marginTop: '6%' }}>
                 <CardContent>
                   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', marginTop: '-30px' }}>
                     <div style={{ display: 'flex', alignItems: 'center' }}>
                       <span style={{ marginRight: '10px', fontSize: '16px', fontWeight: 'bold', color: 'rgb(243, 33, 33)' }}>Alarm(s)</span>
                       {/* You can add more styling as needed */}
                     </div>
                     <div style={{ display: 'flex', alignItems: 'center' }}>
                       <span style={{ marginRight: '10px',fontWeight: 'bold', color: '#2196F3' }}>All Alarm(s)</span>
                       <Switch checked={switchState} onChange={handleSwitchChange} />
                     </div>
                   </div>
                   <div style={{ textAlign: 'right', marginBottom: '10px', marginTop: '10px' }}>
                     <Button
                       variant="outlined"
                       onClick={() => handlePageChange(currentPage - 1)}
                       disabled={currentPage === 1}
                       style={{ color: 'black', fontSize: '12px', height: '25px', minWidth: '25px' }}
                     >
                       Prev
                     </Button>
                     {renderPaginationNumbers()}
                     <Button
                       variant="outlined"
                       onClick={() => handlePageChange(currentPage + 1)}
                       disabled={currentPage === totalPages}
                       style={{ color: 'black', fontSize: '12px', height: '25px', minWidth: '25px' }}
                     >
                       Next
                     </Button>
                   </div>
                   <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                     <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                       <thead>
                         <tr style={{ background: '#f2f2f2', borderBottom: '2px solid #ddd', height: '40px' }}>
                           <StyledTableHeader onClick={() => handleSort('Device Name')}>
                             <OnDeviceTrainingIcon sx={{ verticalAlign: 'middle' }} /> Device Name
                           </StyledTableHeader>
                           <StyledTableHeader onClick={() => handleSort('Sensor')}>
                             <OnDeviceTrainingIcon sx={{ verticalAlign: 'middle' }} /> Sensor
                           </StyledTableHeader>
                           <StyledTableHeader onClick={() => handleSort(' Date/Time begin')}>
                             <AlarmOnIcon sx={{ verticalAlign: 'middle' }} /> Date/Time begin
                           </StyledTableHeader>
                           <StyledTableHeader onClick={() => handleSort('Present status')}>
                             <WarningIcon sx={{ verticalAlign: 'middle' }} /> Present status
                           </StyledTableHeader>
                           <StyledTableHeader onClick={() => handleSort(' Date / time ok')}>
                             <TimerIcon sx={{ verticalAlign: 'middle' }} /> Date / time ok
                           </StyledTableHeader>
                         </tr>
                       </thead>
                       <tbody>
                         {paginatedData().map((rowData, index) => (
   
                           <tr key={index} style={{ borderBottom: '1px solid #ddd', height: '50px' }}>
                             <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px', fontWeight: 'bold' }}>{rowData['Device Name']}</td>
                             <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px' }}>{rowData['Sensor']}</td>
                             <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px' }}>
                               {moment(rowData['Received Date/Time']).isValid()
                                 ? moment(rowData['Received Date/Time']).format('YYYY-MM-DD HH:mm:ss')
                                 : 'NA'}
                             </td>
   
                             <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px' }}>
                               {rowData['Valve Alarm'] === '2' ? (
                                 <div
                                   style={{
                                     backgroundColor: 'rgb(255, 0, 0)',
                                     color: 'white',
                                     fontWeight: 'bold',
                                     padding: '5px 10px',
                                     borderRadius: '3px',
                                     display: 'inline-block',
                                   }}
                                 >
                                   Alarm
                                 </div>
                               ) : rowData['Valve Alarm'] === '1' ? (
                                 <div
                                   style={{
                                     backgroundColor: 'rgb(0, 180, 0)',
                                     color: 'white',
                                     fontWeight: 'bold',
                                     padding: '5px 10px',
                                     borderRadius: '3px',
                                     display: 'inline-block',
                                   }}
                                 >
                                   OK
                                 </div>
                               ) : (
                                 <span style={{ fontSize: '12px' }}>{rowData['Valve Alarm']}</span>
                               )}
                             </td>
                             <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px' }}>
                               {moment(rowData['Received OK Date/Time']).isValid()
                                 ? moment(rowData['Received OK Date/Time']).format('YYYY-MM-DD HH:mm:ss')
                                 : 'NA'}
                             </td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                   </div>
                 </CardContent>
               </Card>
             </form>
           </div>
         </main>
         <Footer />
       </div>
     );
   };
   
   export default Alarm;
   
   